<script setup lang="ts">
import type PropertyListSection from '../PropertyList/PropertyListSection.vue';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { cn } from '@/utils';
import { useIntersectionObserver } from '@vueuse/core';
import { useWindowScrolling } from '~/composables/useWindowScrolling';
import { computed, ref } from 'vue';

import { Button } from '~/components/ui/button';

const props = defineProps<{
	/**
	 * Classes adicionais para o botão
	 */
	class?: string;
	isMenuOpen: boolean;
	/**
	 * Filtros de propriedades atuais
	 */
	scrollElement: InstanceType<typeof PropertyListSection>;
	filters: PropertyFilters;
	isViewMoreButtonInViewport?: boolean;
	viewMoreButtonPosition?: { top: number };
	/**
	 * Elemento que, quando visível na viewport, faz com que o botão seja escondido
	 */
	hideAfter?: HTMLElement | null;
}>();

const emit = defineEmits<{
	/**
	 * Evento emitido ao clicar para ir para a visão completa do mapa
	 */
	'open-map-vision': ['cta'];
}>();

const containerRef = ref<HTMLElement | null>(null);

function handleOpenMapVision() {
	emit('open-map-vision', 'cta');
}

const { y: windowScrollY } = useWindowScrolling();
const scrollElementRef = computed(
	() => props.scrollElement?.$el as HTMLElement | undefined
);
const { y: internalScrollY } = useScroll(scrollElementRef);

const hasReachedHideAfter = ref(false);
useIntersectionObserver(
	() => props.hideAfter,
	entries => {
		const entry = entries[0];
		if (!entry) return;
		hasReachedHideAfter.value =
			entry.isIntersecting || entry.boundingClientRect.top <= 0;
	}
);

// Use internal scroll if the scroll element exists (wishlist case), otherwise use window scroll (main listing)
// No mobile do SRP, o scroll é sempre o da janela (windowScrollY), pois a lista tem overflow:visible.
const currentScrollY = computed(() => {
	// Se estiver na viewport mobile e com a classe bottom-sheet-active no html, o scroll é o do window.
	if (
		import.meta.client &&
		document.documentElement.classList.contains('bottom-sheet-active')
	) {
		return windowScrollY.value;
	}
	return scrollElementRef.value ? internalScrollY.value : windowScrollY.value;
});

const shouldHideAfterElement = computed(() => {
	if (!props.hideAfter) return false;
	return hasReachedHideAfter.value;
});

const isVisible = computed(() => {
	// Se temos um elemento hideAfter e devemos esconder após ele, escondemos o botão
	if (shouldHideAfterElement.value) {
		return false;
	}

	if (!props.isMenuOpen) return false;

	// Se não temos a posição do botão "Ver mais", usamos apenas o estado do menu
	if (!props.viewMoreButtonPosition) return true;

	// Se o botão "Ver mais" está visível na viewport E o usuário já rolou a página,
	// escondemos o botão. Mas se ainda estamos no topo (scroll = 0), mantemos visível
	if (props.isViewMoreButtonInViewport && currentScrollY.value > 0)
		return false;

	// Se já rolamos além da posição do botão "Ver mais", escondemos o botão de mapa
	const viewMoreButtonPosition = props.viewMoreButtonPosition.top;

	// Mostra o botão quando o scroll está acima do botão "Ver mais"
	return currentScrollY.value < viewMoreButtonPosition;
});
</script>

<template>
	<Transition
		enter-active-class="transition-all duration-300 ease-out"
		enter-from-class="opacity-0 transform translate-y-4"
		enter-to-class="opacity-100 transform translate-y-0"
		leave-active-class="transition-all duration-300 ease-in"
		leave-from-class="opacity-100 transform translate-y-0"
		leave-to-class="opacity-0 transform translate-y-4"
	>
		<div
			v-show="isVisible"
			ref="containerRef"
			:class="
				cn(
					'fixed bottom-4 left-1/2 z-40 -translate-x-1/2 flex flex-col items-center lg:hidden',
					props.class
				)
			"
		>
			<div class="mb-2 flex w-full flex-col items-center gap-2">
				<Button
					variant="primary"
					size="sm"
					class="flex w-full items-center gap-2 shadow-lg"
					@click="handleOpenMapVision"
				>
					<IconMaterialSymbolsLightMapOutline class="size-[23px] text-beige" />
					<span>Ver mapa</span>
				</Button>
			</div>
		</div>
	</Transition>
</template>
